import { useEffect, useMemo, useState } from "react"
import { PanelWrapper, FormField, Data, useUIStateActions, FieldType, FieldValue, option, ModalId, useUIActions, useGetCrumbs, TextArea, O, FileField, FileType, useEffectAfterMount } from "../../../../../features/ui"
import { TableName } from "../../../../../tables"
import { useActions } from "./actions"
import { DataTypeForm2, Labels, QuickFormModal, Standards } from "../../../../../features/data-types"
import { BranchBadge, useManagerState } from "../../../../manager"
import { Supplier, SupplierQuickForm, useSupplierLoaders } from "../../../../purchases"
import { Expense, TaxType, TicketType } from "../../../state/types"
import { useNavigate } from "react-router-dom"
import { RoutePath, useRoutesHooks } from "../../../../../features/routes"
import { fileHelpers } from '../../../../../features/files'
import { TaxItemsTable } from '../../tax/tax-items-table/taxItemsTable'
import { useTaxLoaders } from '../../../loaders/taxLoaders'
import { TaxItemFormModal } from '../../tax/tax-item-form-modal/taxItemFormModal'
import { useExpenseTypeLoaders } from '../../../loaders/expenseTypeLoaders'

export const ExpenseForm = () => {
    const { dataType } = useRoutesHooks().parseUrl()
    const expense = dataType as O<Expense>
    const { currentBranch } = useManagerState()
    const branch = currentBranch!
    
    const [registeredSupplier, setRegisteredSupplier] = useState<boolean>()
    const [supplierId, setSupplierId] = useState<O<string>>()
    const [refundable, setRefundable] = useState<boolean>()
    const [refundEmployeeId, setRefundEmployeeId] = useState<O<string>>()
    const [detail, setDetail] = useState<O<string>>()
    const [ticketType, setTicketType] = useState<O<TicketType>>(TicketType.TICKET_A)
    const [ticketFile, setTicketFile] = useState<O<File>>()
    const [ticketDueDate, setTicketDueDate] = useState<O<Date>>(new Date())
    const [subtotal, setSubtotal] = useState<O<number>>()
    const [vatAmount, setVatAmount] = useState<O<number>>(0)
    const [taxItemsData, setTaxItemsData] = useState<Data[]>([])
    const [currentTaxItem, setCurrentTaxItem] = useState<Data>()
    const [total, setTotal] = useState<O<number>>()
    
    const { expenseTicketFileName } = fileHelpers
    const navigate = useNavigate()

    const expenseTypeLoaders = useExpenseTypeLoaders()
    const supplierLoaders = useSupplierLoaders()
    const taxLoaders = useTaxLoaders()

    const { submitExpense } = useActions()
    const { setDisableBranchSelector } = useUIStateActions()
    const { openModal } = useUIActions()

    useEffect(() => {
        setDisableBranchSelector(true)
        return () => {
            setDisableBranchSelector(false)
        }
    }, [])

    useEffectAfterMount(() => {
        subtotal && setTotal(subtotal + (vatAmount || 0) + taxItemsData.reduce((sum, taxItemData) => sum + (taxItemData.amount! as number), 0))
    }, [subtotal, vatAmount, taxItemsData])

    const suppliers = useMemo(() => supplierLoaders().findAllSuppliers()
        .filter(supplier => !supplier.productSupplier && (supplier.id === supplierId || !supplier.archived))
    , [supplierId])
    
    const crumbs = useGetCrumbs(
        'Gastos',
        RoutePath.EXPENSES,
        undefined,
        undefined,
        'Editar'
    )
    
    const isNonTax = (type?: TicketType) => type && [TicketType.TICKET_B, TicketType.TICKET_C, TicketType.RECEIPT_X].includes(type)
    const taxItemMatches = (data: Data, taxCode: string) => data.taxCode === taxCode
    const typeOptions = useMemo(() => expenseTypeLoaders().findAllExpenseTypes()
        .filter(expenseType => expenseType.name !== Standards.SalaryExpenseTypeName && (
            !expenseType.archived || expenseType.id === expense?.expenseTypeId
        ))
        .map(type => option(type.id, type.name))
    , [])
    const perceptions = useMemo(() => taxLoaders().findAllTaxes().filter(tax =>
        tax.type === TaxType.PERCEPTION && (
            !tax.archived && !taxItemsData.map(taxItemData => taxItemData.taxCode).includes(tax.code)
        )
    ), [taxItemsData, currentTaxItem])

    const onRegisteredSupplierChange = (value?: FieldValue) => setRegisteredSupplier(!!value)

    const onRefundableChange = (value?: FieldValue) => setRefundable(!!value)
    
    const onDetailChange = (value?: FieldValue) => setDetail(value as O<string>)

    const onChangeSupplier = (value?: FieldValue) => setSupplierId(value as O<string>)

    const onChangeRefundEmployee = (value?: FieldValue) => setRefundEmployeeId(value as O<string>)

    const onCreateNewSupplier = () => openModal(ModalId.QUICK_FORM)

    const onChangeTicketType = (value?: FieldValue) => {
        const newTicketType = value as O<TicketType>
        setTicketType(newTicketType)
        setSubtotal(total || 0)
        setVatAmount(0)
        setTaxItemsData([])
    }

    const onChangeTicketDueDate = (value?: FieldValue) => setTicketDueDate(value as O<Date>)

    const onChangeSubtotal = (value?: FieldValue) => setSubtotal(value as O<number>)

    const onChangeVatAmount = (value?: FieldValue) => setVatAmount(value as O<number>)

    const onAddTaxItem = () => {
        setCurrentTaxItem(undefined)
        openModal(ModalId.TAX_ITEM_FORM)
    }
    
    const onEditTaxItem = (taxCode: string) => {
        setCurrentTaxItem(taxItemsData.find(taxItemData => taxItemMatches(taxItemData, taxCode)))
        openModal(ModalId.TAX_ITEM_FORM)
    }

    const onRemoveTaxItem = (taxCode: string) => {
        const newTaxItemsData = taxItemsData.filter(taxItemData => !taxItemMatches(taxItemData, taxCode))
        setTaxItemsData(newTaxItemsData)
    }

    const onChangeTotal = (value?: FieldValue) => {
        const newTotal = value as O<number>
        setTotal(newTotal)
        isNonTax(ticketType) && setSubtotal(newTotal)
    }

    const fields: FormField[] = [
        {
            name: 'expenseTypeId',
            type: FieldType.TABLE,
            table: TableName.EXPENSE_TYPES,
            label: 'Tipo',
            options: typeOptions
        },
        {
            name: 'name',
            type: FieldType.TEXT,
            label: 'Nombre'
        },
        {
            name: 'branchId',
            type: FieldType.TABLE,
            table: TableName.BRANCHES,
            label: 'Sucursal',
            render: () => <BranchBadge branchId={branch.id} />
        },
        {
            name: 'registeredSupplier',
            type: FieldType.BOOLEAN,
            label: 'Establecer Proveedor',
            defaultValue: registeredSupplier,
            onChange: onRegisteredSupplierChange
        },
        {
            name: 'refundable',
            type: FieldType.BOOLEAN,
            label: 'Reembolsable a Empleado',
            defaultValue: refundable,
            onChange: onRefundableChange
        },
        {
            name: 'date',
            type: FieldType.DATE,
            label: 'Fecha',
            defaultValue: new Date(),
            max: new Date()
        },
        {
            name: 'detail',
            type: FieldType.TEXT,
            label: 'Detalle',
            render: () => <TextArea
                id="detail"
                defaultValue={detail}
                optional
                onChange={onDetailChange}
            />
        },
        {
            name: 'ticketType',
            type: FieldType.SELECT,
            label: 'Tipo de Factura',
            defaultValue: ticketType,
            options: Object.values(TicketType)
                .filter(type => type !== TicketType.SALARY)
                .map(type => option(type, Labels.ticketTypeLabel(type))),
            onChange: onChangeTicketType
        },
        {
            name: 'ticketUrl',
            type: FieldType.FILE,
            label: 'Factura / Comprobante',
            hint: 'Formato: PDF, JPG, PNG',
            render: () => (
                <FileField
                    id="ticketUrl"
                    value={ticketFile}
                    fileName={expenseTicketFileName()}
                    fileTypes={[FileType.PDF, FileType.JPEG, FileType.PNG]}
                    onChange={setTicketFile}
                />
            )
        },
        {
            name: 'ticketDueDate',
            type: FieldType.DATE,
            label: 'Fecha de Vto. de Factura',
            defaultValue: ticketDueDate,
            min: new Date(),
            onChange: onChangeTicketDueDate
        },
        {
            name: 'total',
            type: FieldType.PRICE,
            label: 'Monto Final',
            value: total,
            onChange: onChangeTotal
        }
    ]
    
    let extraFields = 0
    if (registeredSupplier) {
        const supplierOptions = suppliers.map(supplier => option(supplier.id, supplier.name))
        const supplierField = {
            name: 'supplierId',
            type: FieldType.TABLE,
            table: TableName.SUPPLIERS,
            value: supplierId,
            label: 'Proveedor',
            options: supplierOptions,
            onChange: onChangeSupplier,
            onCreateNew: onCreateNewSupplier
        }

        fields.splice(4, 0, supplierField)
        extraFields++
    }
    if (refundable) {
        const refundEmployeeField = {
            name: 'refundEmployeeId',
            type: FieldType.TABLE,
            table: TableName.EMPLOYEES,
            value: refundEmployeeId,
            label: 'Empleado',
            onChange: onChangeRefundEmployee,
        }

        fields.splice(5 + extraFields, 0, refundEmployeeField)
        extraFields++
    }
    if (!isNonTax(ticketType)) {
        const subtotalField = {
            name: 'subtotal',
            type: FieldType.PRICE,
            label: 'Subtotal Productos',
            value: subtotal,
            onChange: onChangeSubtotal
        }
        const vatAmountField = {
            name: 'vatAmount',
            type: FieldType.PRICE,
            label: 'IVA',
            value: vatAmount,
            onChange: onChangeVatAmount
        }
        const taxItemsField = {
            name: 'taxItems',
            type: FieldType.TABLE,
            label: 'Percepciones',
            render: () => (
                <TaxItemsTable
                    taxItems={taxItemsData}
                    disableCreate={!subtotal || perceptions.length === taxItemsData.length}
                    onCreate={onAddTaxItem}
                    onEditRow={onEditTaxItem}
                    onRemoveRow={onRemoveTaxItem}
                />
            )
        }
        fields.splice(10 + extraFields, 0, subtotalField, vatAmountField, taxItemsField)
    }

    const onSubmit = (data: Data) => {
        submitExpense(
            data,
            branch.id,
            subtotal || 0,
            vatAmount || 0,
            taxItemsData,
            registeredSupplier ? data.supplierId as string : undefined,
            refundable ? data.refundEmployeeId as string : undefined,
            detail,
            ticketFile
        )
    }

    const onCancel = () => navigate(-1)

    const onSubmitSupplier = (supplier: Supplier) => {
        setSupplierId(supplier.id)
    }

    const onSaveTaxItem = (newData: Data) => {
        let newTaxItemsData = taxItemsData
        if (currentTaxItem) {
            const { taxCode } = currentTaxItem
            newTaxItemsData = taxItemsData.map(taxItemData => taxItemMatches(taxItemData, taxCode! as string) ? newData : taxItemData)
        } else {
            newTaxItemsData = [newData, ...taxItemsData]
        }
        setTaxItemsData(newTaxItemsData)
    }
    
    return (
        <PanelWrapper title="Crear Gasto" crumbs={crumbs}>
            <DataTypeForm2
               formId="expense-form"
               fields={fields}
               createMode
               onSubmit={onSubmit}
               onCancel={onCancel}
            />
            <QuickFormModal title="Crear Proveedor">
                <SupplierQuickForm
                    productSupplier={false}
                    onSubmit={onSubmitSupplier}
                />
            </QuickFormModal>
            <TaxItemFormModal
                taxItem={currentTaxItem}
                total={subtotal}
                title="Agregar Percepción"
                editableAmount
                taxes={perceptions}
                onSubmit={onSaveTaxItem}
            />
        </PanelWrapper>
    )
}
